import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Menu, X, LogOut, User, LogIn, MessageCircle,
  Code2, Palette, Bug, BarChart3, Brain, Cloud, Layers, Target, Users, Shield, Settings, Database, TrendingUp, FileText,
  Building2, Heart, ShoppingBag, ShoppingCart, GraduationCap, Factory, Home, Truck, Film, Landmark, Rocket
} from "lucide-react";
import { useState, useEffect, useRef, type ReactElement } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useCart } from "@/contexts/CartContext";
import { toast } from "@/hooks/use-toast";
import Logo from "./Logo";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const { user, signOut, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Cart management using new context
  const cart = useCart();
  const hasItems = cart.itemCount > 0;

  // Function to split items into columns for multi-column layout
  const splitIntoColumns = <T,>(items: T[], maxItemsPerColumn = 4): T[][] => {
    if (items.length <= maxItemsPerColumn) {
      return [items];
    }
    
    const columns = [];
    for (let i = 0; i < items.length; i += maxItemsPerColumn) {
      columns.push(items.slice(i, i + maxItemsPerColumn));
    }
    return columns;
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      toast({
        title: "Sign Out Error",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

      const menuItems = [
        {
          title: "How it Works",
          href: "/how-it-works",
          items: []
        },
        {
          title: "About Us",
          href: "/about",
          items: [
            { label: "Why NearSmarter", href: "/about", icon: Target },
            { label: "Luby Software", href: "/why/powered-by-luby", icon: Building2 },
            { label: "Clients", href: "/insights/client-success", icon: Users },
          ]
        },
        {
          title: "Expertise",
          href: "/stack-selection",
          items: [
            { label: "Infrastructure", href: "/stack-selection/infrastructure", icon: Settings },
            { label: "Development", href: "/stack-selection/development", icon: Code2 },
            { label: "Q&A", href: "/stack-selection/qa", icon: Bug },
            { label: "CyberSecurity", href: "/stack-selection/cybersecurity", icon: Shield },
            { label: "Data", href: "/stack-selection/data", icon: BarChart3 },
            { label: "Artificial Intelligence", href: "/stack-selection/artificial-intelligence", icon: Brain },
            { label: "Product Management", href: "/stack-selection/product-management", icon: Target },
            { label: "UI/UX", href: "/stack-selection/ui-ux", icon: Palette },
            { label: "Project Management", href: "/stack-selection/project-management", icon: Users },
          ]
        },
        {
          title: "Industries",
          href: "/solutions",
          items: [
            { label: "Fintech", href: "/solutions/fintech", icon: Building2 },
            { label: "Healthcare", href: "/solutions/healthcare", icon: Heart },
            { label: "E-commerce", href: "/solutions/ecommerce", icon: ShoppingBag },
            { label: "Education", href: "/solutions/education", icon: GraduationCap },
            { label: "Manufacturing", href: "/solutions/manufacturing", icon: Factory },
            { label: "Real Estate", href: "/solutions/real-estate", icon: Home },
            { label: "Logistics", href: "/solutions/logistics", icon: Truck },
            { label: "Entertainment", href: "/solutions/media", icon: Film },
            { label: "Startups", href: "/solutions/startups", icon: Rocket },
          ]
        }
      ];

  return (
    <div className="fixed top-8 left-0 right-0 w-full" style={{ zIndex: 9998 }}>
      <div className="container mx-auto px-6">
        <nav className="bg-[#5C626B]/30 backdrop-blur-md rounded-lg shadow-lg relative overflow-visible" style={{ position: 'relative', transform: 'translateZ(0)' }}>
          <div className="flex items-center justify-between h-[4.9rem] px-4 md:px-8 relative z-10">
          {/* Logo */}
          <Link to="/" className="flex items-center hover:opacity-80 transition-opacity flex-shrink-0 mr-4 md:mr-6">
            <img 
              src="/logo-nearsmarter-navbar.webp" 
              alt="NearSmarter Logo" 
              className="h-8 md:h-10 w-auto object-contain flex-shrink-0"
              width={200}
              height={40}
              style={{ maxWidth: 'none' }}
            />
          </Link>

              {/* Desktop Navigation */}
              <NavigationMenu className="hidden lg:flex flex-1 justify-start ml-8">
                <NavigationMenuList>
                  {menuItems.map((menu, index) => {
                    // If menu has no items, render as simple link
                    if (menu.items.length === 0) {
                      return (
                        <NavigationMenuItem key={index}>
                          <NavigationMenuLink
                            asChild
                            className={cn(
                              navigationMenuTriggerStyle(),
                              "bg-transparent text-white hover:text-white/80 hover:bg-white/10 h-10 font-medium"
                            )}
                          >
                            <Link to={menu.href}>{menu.title}</Link>
                          </NavigationMenuLink>
                        </NavigationMenuItem>
                      );
                    }
                    
                    // If menu has items, render with NavigationMenu
                    const columns = splitIntoColumns(menu.items);
                    const isMultiColumn = columns.length > 1;
                    
                    return (
                      <NavigationMenuItem key={index}>
                        <NavigationMenuTrigger
                          className={cn(
                            navigationMenuTriggerStyle(),
                            "bg-transparent text-white hover:text-white/80 hover:bg-white/10 h-10 font-medium"
                          )}
                        >
                          {menu.title}
                        </NavigationMenuTrigger>
                        <NavigationMenuContent className="bg-white">
                          <ul className={`grid ${isMultiColumn ? 'grid-cols-3 w-[800px] gap-x-4' : 'grid-cols-1 w-64'} p-4`}>
                            {isMultiColumn ? (
                              columns.map((column, columnIndex) => (
                                <div key={columnIndex} className="flex flex-col space-y-0.5">
                                  {column.map((item, itemIndex) => (
                                    <ListItem
                                      key={itemIndex}
                                      title={item.label}
                                      icon={item.icon}
                                      href={item.href}
                                    />
                                  ))}
                                </div>
                              ))
                            ) : (
                              <div className="space-y-0.5">
                                {menu.items.map((item, itemIndex) => (
                                  <ListItem
                                    key={itemIndex}
                                    title={item.label}
                                    icon={item.icon}
                                    href={item.href}
                                  />
                                ))}
                              </div>
                            )}
                          </ul>
                        </NavigationMenuContent>
                      </NavigationMenuItem>
                    );
                  })}
                  <NavigationMenuItem>
                    <NavigationMenuLink
                      asChild
                      className={cn(
                        navigationMenuTriggerStyle(),
                        "bg-transparent text-white hover:text-white/80 hover:bg-white/10 h-10 font-medium"
                      )}
                    >
                      <Link to="/insights">Insights</Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>

              {/* Contact Us and Login/User Menu Buttons */}
              <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
                <Button className="bg-[#41A0DC] !text-[#0F2341] hover:bg-[#41A0DC]/90 hover:!text-[#0F2341] px-2 md:px-4 py-2 h-8 font-medium !transition-colors !transform-none shadow-sm text-small rounded-lg hidden sm:inline-flex" asChild>
                  <Link to="/contact" className="!text-[#0F2341]">
                    Contact Us
                  </Link>
                </Button>

                {user ? (
                  <NavigationMenu>
                    <NavigationMenuList>
                      <NavigationMenuItem>
                        <NavigationMenuTrigger
                          className={cn(
                            navigationMenuTriggerStyle(),
                            "bg-transparent text-white hover:text-white/80 hover:bg-white/10 h-10 font-medium"
                          )}
                        >
                          {user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0] || 'User'}
                        </NavigationMenuTrigger>
                        <NavigationMenuContent className="bg-white">
                          <ul className="grid grid-cols-1 w-64 p-4">
                            <div className="space-y-0.5">
                              <ListItem
                                title="My Squads"
                                icon={Users}
                                href="/my-squads"
                              />
                              <li>
                                <button
                                  onClick={handleSignOut}
                                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-foreground hover:bg-muted rounded-md transition-colors"
                                >
                                  <LogOut className="w-4 h-4 text-primary flex-shrink-0" />
                                  <span>Logout</span>
                                </button>
                              </li>
                            </div>
                          </ul>
                        </NavigationMenuContent>
                      </NavigationMenuItem>
                    </NavigationMenuList>
                  </NavigationMenu>
                ) : (
                  <Button 
                    variant="outline" 
                    className="border-white text-white hover:bg-white/10 hover:text-white px-2 md:px-4 py-2 h-8 font-medium !transition-colors !transform-none text-small rounded-lg" 
                    asChild
                  >
                    <Link to="/auth" state={{ from: location }}>
                      <span className="hidden sm:inline">Login</span>
                      <span className="sm:hidden">Log</span>
                    </Link>
                  </Button>
                )}

                {/* Active Cart Icon - Go to Calculator */}
                {hasItems && (
                  <Link
                    to="/calculator"
                    className="relative px-2 md:px-4 py-2 rounded-md bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-colors flex items-center gap-2 flex-shrink-0"
                    data-testid="cart-icon"
                  >
                    <ShoppingCart className="h-4 w-4 text-white flex-shrink-0" />
                    <span className="hidden md:inline text-white text-small">My New Squad</span>
                    {cart.itemCount > 0 && (
                      <Badge className="absolute -top-1 -right-1 bg-[#41A0DC] text-white h-5 min-w-5 flex items-center justify-center p-1 text-xs rounded-full" data-testid="cart-badge">
                        {cart.itemCount}
                      </Badge>
                    )}
                  </Link>
                )}
              </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
              className="text-white hover:bg-white/10 !transition-colors !transform-none"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

            {/* Mobile Navigation */}
            {isMenuOpen && (
              <div className="lg:hidden absolute top-full left-0 right-0 bg-[#5C626B]/30 backdrop-blur-md rounded-2xl mt-2 mx-4 shadow-lg">
                <div className="py-6 px-6 space-y-6">
                  {menuItems.map((menu, index) => (
                    <div key={index} className="space-y-3">
                      <Link 
                        to={menu.href}
                        className="text-white text-small font-medium hover:text-white/80 transition-colors"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {menu.title}
                      </Link>
                      {menu.items.length > 0 && (
                        <div className="pl-4 space-y-2">
                          {menu.items.map((item, itemIndex) => {
                            const IconComponent = item.icon;
                            return (
                              <Link
                                key={itemIndex}
                                to={item.href}
                                className="flex items-center gap-3 py-2 text-white/80 hover:text-white hover:bg-white/10 rounded px-3 transition-colors text-small"
                                onClick={() => setIsMenuOpen(false)}
                              >
                                <IconComponent className="w-4 h-4 text-[#41A0DC] flex-shrink-0" />
                                <span>{item.label}</span>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                  
                  <Link
                    to="/insights"
                    className="block py-2 text-white text-small font-medium hover:text-white/80 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Insights
                  </Link>
              
                  {/* Contact Us and Login/User Menu Buttons Mobile */}
                  <div className="space-y-3">
                    <Button className="w-full bg-[#41A0DC] text-[#0F2341] hover:bg-[#41A0DC]/90 hover:text-[#0F2341] !transition-colors !transform-none shadow-sm rounded-full text-small" size="sm" asChild>
                      <Link to="/contact" onClick={() => setIsMenuOpen(false)}>
                        Contact Us
                      </Link>
                    </Button>

                    {user ? (
                      <>
                        <Button 
                          variant="outline" 
                          className="w-full border-white text-white hover:bg-white/10 hover:text-white !transition-colors !transform-none text-small rounded-full" 
                          size="sm" 
                          onClick={() => {
                            navigate('/my-squads');
                            setIsMenuOpen(false);
                          }}
                        >
                          <Users className="w-3 h-3 mr-2" />
                          My Squads
                        </Button>
                        <Button 
                          variant="outline" 
                          className="w-full border-white text-white hover:bg-white/10 hover:text-white !transition-colors !transform-none text-small rounded-full" 
                          size="sm" 
                          onClick={() => {
                            handleSignOut();
                            setIsMenuOpen(false);
                          }}
                        >
                          <LogOut className="w-3 h-3 mr-2" />
                          Logout
                        </Button>
                      </>
                    ) : (
                      <Button 
                        variant="outline" 
                        className="w-full border-white text-white hover:bg-white/10 hover:text-white !transition-colors !transform-none text-small rounded-full"
                        size="sm" 
                        asChild
                      >
                        <Link to="/auth" state={{ from: location }} onClick={() => setIsMenuOpen(false)}>
                          Login
                        </Link>
                      </Button>
                    )}
                  </div>
            </div>
          </div>
        )}
          </nav>
        </div>
      </div>
    );
};

function ListItem({
  title,
  icon,
  href,
  ...props
}: React.ComponentPropsWithoutRef<"li"> & {
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}) {
  const IconComponent = icon;
  return (
    <li {...props}>
      <NavigationMenuLink asChild className="hover:bg-transparent">
        <Link to={href}>
          <div className="flex gap-3 items-center rounded-md px-3 py-2.5 hover:bg-primary/10 transition-colors">
            <IconComponent className="w-4 h-4 text-[#41A0DC] flex-shrink-0" />
            <div className="text-sm font-medium leading-none text-[#0F2341]">{title}</div>
          </div>
        </Link>
      </NavigationMenuLink>
    </li>
  );
}

export default Navigation;
